//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { showToastMessage } from "../../utils/app-utils";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/icons/jha-icon-plus";
import "@banno/jha-wc/src/icons/jha-icon-edit";
import "@banno/jha-wc/src/icons/jha-icon-delete";
import "@banno/jha-wc/src/icons/jha-icon-information";
import "@banno/jha-wc/src/jha-tooltip/jha-tooltip";
import "@jack-henry/jh-ui/components/tooltip/tooltip.js";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import "../../Components/TemplateStringInput";
import { WorkflowTemplateVariable } from "../../types/WorkflowTemplates/WorkflowTemplateVariable";

/**
 * Workflow Template File Pattern View
 *
 * A file pattern management component specifically designed for workflow templates.
 * Uses template-string-input to allow file patterns to include template variables.
 *
 * Features:
 * - Add/Edit/Delete trigger and data file patterns
 * - Supports template variables (e.g., {businessUnit}/*.txt)
 * - Variables can be inserted via the variable picker or by typing '{'
 * - Patterns are displayed in a list with edit/delete actions
 *
 * This is a template-specific version of FilePatternView.ts that adds variable support.
 */
@customElement("workflow-template-file-pattern-view")
export class WorkflowTemplateFilePatternView extends formElementMixin(LitElement) {
    static get is() {
        return "workflow-template-file-pattern-view";
    }

    // Variable support - passed from parent tab
    @property({ type: Array }) variables: WorkflowTemplateVariable[] = [];

    // Form data binding - uses camelCase to match API contract
    @property({ type: Object })
    formData: {
        triggerFilePattern: string[];
        dataFilePattern: string[];
        valid: boolean;
        isEdit: boolean;
    } = {
            triggerFilePattern: [],
            dataFilePattern: [],
            valid: false,
            isEdit: false,
        };

    // Read-only mode
    @property({ type: Boolean }) isReadOnly: boolean = false;

    // Internal state for pattern lists
    @state() triggerFileList: string[] = [];
    @state() dataFileList: string[] = [];

    // Current input values
    @state() triggerFileName: string = "";
    @state() dataFileName: string = "";

    // Edit state tracking
    @state() editTriggerPattern: boolean = false;
    @state() editDataPattern: boolean = false;
    @state() editTriggerFilePatternName: string = "";
    @state() editDataFilePatternName: string = "";

    // Force update trigger
    @state() toUpdate: string = "update";

    static readonly styles = css`
    :host {
      display: block;
    }

    #patternDiv {
      display: flex;
      flex-direction: row;
      column-gap: var(--file-pattern-column-gap, 48px);
    }

    #triggerFileDiv,
    #dataFileDiv {
      display: flex;
      flex-direction: column;
    }

    #triggerFileInput,
    #dataFileInput {
      display: flex;
      flex-direction: row;
      column-gap: 8px;
      align-items: flex-start;
    }

    /* Wrapper for input to ensure consistent flex behavior */
    .input-wrapper {
      display: flex;
      align-items: flex-start;
      flex-shrink: 0;
    }

    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }

    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }

    h2 {
      margin-top: 24px;
      margin-bottom: 24px;
      font-size: 24px;
      font-weight: 600;
    }

    /* Pattern list styling - match original FilePatternView */
    .pattern-field {
      width: 25vw;
      flex-shrink: 0;
    }

    .pattern-list ul {
      list-style: disc;
      width: 100%;
      box-sizing: border-box;
      margin: 8px 0 0;
      /* Lines the icons up with the text field: the 40px variable button, the 8px gap, and 10px of extra space. */
      padding: 0 58px 0 0;
    }

    .pattern-item {
      display: flex;
      align-items: center;
    }

    .pattern-item > jha-tooltip:first-of-type {
      margin-left: auto;
    }

    .pattern-item li {
      font-size: large;
      margin-left: 33px;
    }

    .pattern-item span {
      display: inline-block;
      width: auto;
      max-width: 10em;
      vertical-align: middle;
    }

    span {
      white-space: nowrap;
      text-overflow: ellipsis;
      width: 10em;
      display: block;
      overflow: hidden;
    }

    /* Template string input sizing - match jh-input from original */
    template-string-input {
      width: 100%;
    }

    /* Icon container - positioned to align with input field center (label ~18px + half input ~20px = ~38px from top) */
    .icon-container {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      width: 40px;
      flex-shrink: 0;
      margin-top: 18px;
    }

    /* Tooltip version of icon container - same positioning without the wrapper div */
    jh-tooltip.icon-container-inline {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      width: 40px;
      flex-shrink: 0;
      margin-top: 18px;
    }

    /* Ensure icons inside containers are centered consistently */
    .icon-container jha-icon-information {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .icon-container jha-button {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      width: 40px;
      padding: 0;
      margin: 0;
    }

    /* Tooltip styling - consistent padding */
    .filePatternTooltip {
      margin-top: 13px;
      position: absolute;
      border-radius: 6px;
      padding: 12px;
      width: max-content;
      max-width: min(36rem, 70vw);
      height: auto;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      background: #0d0d0d;
      color: white;
      box-sizing: border-box;
      z-index: 1061;
      display: none;
      font-size: 13px;
      border: 1px solid #333;
    }

    .filePatternTooltip ul {
      margin: 0;
      padding-left: 20px;
      list-style: disc;
    }

    .filePatternTooltip li {
      margin-bottom: 6px;
      white-space: normal;
    }

    .filePatternTooltip li:last-child {
      margin-bottom: 0;
    }

    .iconInfo:hover + .filePatternTooltip {
      display: block;
      position: absolute;
      top: 100%;
      z-index: 1061;
    }

    .filePatternTooltip.data-tooltip {
      left: 0;
    }

    .filePatternTooltip.trigger-tooltip {
      right: 0;
      left: auto;
    }

    @media (prefers-color-scheme: light) {
      .filePatternTooltip {
        background: #111;
        border: 1px solid #222;
      }
    }
  `;

    async connectedCallback() {
        super.connectedCallback();
        this.syncFromFormData();
    }

    /**
     * Sync internal lists when formData property changes
     */
    updated(changedProperties: Map<string, any>) {
        super.updated(changedProperties);

        if (changedProperties.has("formData")) {
            this.syncFromFormData();
        }
    }

    /**
     * Sync internal state from formData prop
     */
    private syncFromFormData() {
        const newTrigger = this.formData.triggerFilePattern || [];
        const newData = this.formData.dataFilePattern || [];

        if (JSON.stringify(this.triggerFileList) !== JSON.stringify(newTrigger)) {
            this.triggerFileList = [...newTrigger];
        }

        if (JSON.stringify(this.dataFileList) !== JSON.stringify(newData)) {
            this.dataFileList = [...newData];
        }
    }

    /**
     * Get variable options formatted for template-string-input (sorted alphabetically)
     */
    get variableOptions() {
        return (this.variables ?? [])
            .map((v) => ({
                name: v.name,
                value: v.name,
                description: v.description,
            }))
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    }

    /**
     * Handle variable-added event from template-string-input
     * Bubble up to parent so it can add to Settings tab
     */
    onVariableAdded(e: CustomEvent) {
        e.stopPropagation();
        this.dispatchEvent(
            new CustomEvent("variable-added", {
                detail: e.detail,
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Dispatch file pattern change event to parent
     * Includes midEdit state to indicate if user has typed a pattern but not clicked "+"
     */
    private dispatchFilePatternChange() {
        // Check if either input has a value that hasn't been added to the list
        const hasPendingTriggerInput = (this.triggerFileName || "").trim().length > 0;
        const hasPendingDataInput = (this.dataFileName || "").trim().length > 0;
        const midEdit = hasPendingTriggerInput || hasPendingDataInput;

        this.dispatchEvent(
            new CustomEvent("onFilePatternChange", {
                detail: {
                    triggerFilePattern: this.triggerFileList,
                    dataFilePattern: this.dataFileList,
                    valid: this.formData.valid,
                    midEdit,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    // ============================================
    // Trigger File Pattern Handlers
    // ============================================

    onTriggerFileNameChange(e: CustomEvent) {
        this.triggerFileName = e.detail.value || "";
        // Dispatch change to update midEdit state as user types
        this.dispatchFilePatternChange();
    }

    onFilePatternKeyDown(e: KeyboardEvent, add: () => void) {
        if (e.key !== "Enter" || e.isComposing || this.isReadOnly) return;
        e.preventDefault();
        add();
    }

    onTriggerFileAdd() {
        if (!this.triggerFileName || this.triggerFileName.trim() === "") {
            return;
        }

        const pattern = this.triggerFileName.trim();
        const isAlreadyPresent = this.triggerFileList.find((r) => r.toLowerCase() === pattern.toLowerCase());

        if (isAlreadyPresent) {
            showToastMessage("Pattern already exists.", "danger");
            return;
        }

        this.triggerFileList = [...this.triggerFileList, pattern];
        this.triggerFileName = "";
        this.editTriggerPattern = false;
        this.formData.triggerFilePattern = this.triggerFileList;
        this.toUpdate = this.toUpdate === "" ? "update" : "";
        this.dispatchFilePatternChange();
    }

    onEditTriggerName(pattern: string) {
        // Check if pattern exists before editing
        if (!this.triggerFileList.includes(pattern)) {
            return;
        }

        this.triggerFileName = pattern;
        this.editTriggerPattern = true;
        this.editTriggerFilePatternName = pattern;

        // Remove from list while editing
        this.triggerFileList = this.triggerFileList.filter((p) => p !== pattern);
        this.formData.triggerFilePattern = this.triggerFileList;
        this.toUpdate = this.toUpdate === "" ? "update" : "";
        this.dispatchFilePatternChange();
    }

    onDeleteTriggerName(pattern: string) {
        this.triggerFileList = this.triggerFileList.filter((p) => p !== pattern);
        this.formData.triggerFilePattern = this.triggerFileList;

        if (pattern === this.triggerFileName) {
            this.triggerFileName = "";
            this.editTriggerPattern = false;
        }

        this.toUpdate = this.toUpdate === "" ? "update" : "";
        this.dispatchFilePatternChange();
    }

    onTriggerFileClear() {
        if (this.editTriggerPattern && this.editTriggerFilePatternName) {
            // Restore the original pattern if canceling edit
            this.triggerFileList = [...this.triggerFileList, this.editTriggerFilePatternName];
            this.formData.triggerFilePattern = this.triggerFileList;
            this.editTriggerPattern = false;
            this.editTriggerFilePatternName = "";
            this.dispatchFilePatternChange();
        }
        this.triggerFileName = "";
        this.toUpdate = this.toUpdate === "" ? "update" : "";
    }

    // ============================================
    // Data File Pattern Handlers
    // ============================================

    onDataFileNameChange(e: CustomEvent) {
        this.dataFileName = e.detail.value || "";
        // Dispatch change to update midEdit state as user types
        this.dispatchFilePatternChange();
    }

    onDataFileAdd() {
        if (!this.dataFileName || this.dataFileName.trim() === "") {
            return;
        }

        const pattern = this.dataFileName.trim();
        const isAlreadyPresent = this.dataFileList.find((r) => r.toLowerCase() === pattern.toLowerCase());

        if (isAlreadyPresent) {
            showToastMessage("Pattern already exists.", "danger");
            return;
        }

        this.dataFileList = [...this.dataFileList, pattern];
        this.dataFileName = "";
        this.editDataPattern = false;
        this.formData.dataFilePattern = this.dataFileList;
        this.toUpdate = this.toUpdate === "" ? "update" : "";
        this.dispatchFilePatternChange();
    }

    onEditDataName(pattern: string) {
        this.dataFileName = pattern;
        this.editDataPattern = true;
        this.editDataFilePatternName = pattern;

        // Remove from list while editing
        this.dataFileList = this.dataFileList.filter((p) => p !== pattern);
        this.formData.dataFilePattern = this.dataFileList;
        this.toUpdate = this.toUpdate === "" ? "update" : "";
        this.dispatchFilePatternChange();
    }

    onDeleteDataName(pattern: string) {
        this.dataFileList = this.dataFileList.filter((p) => p !== pattern);
        this.formData.dataFilePattern = this.dataFileList;

        if (pattern === this.dataFileName) {
            this.dataFileName = "";
            this.editDataPattern = false;
        }

        this.toUpdate = this.toUpdate === "" ? "update" : "";
        this.dispatchFilePatternChange();
    }

    onDataFileClear() {
        if (this.editDataPattern && this.editDataFilePatternName) {
            // Restore the original pattern if canceling edit
            this.dataFileList = [...this.dataFileList, this.editDataFilePatternName];
            this.formData.dataFilePattern = this.dataFileList;
            this.editDataPattern = false;
            this.editDataFilePatternName = "";
            this.dispatchFilePatternChange();
        }
        this.dataFileName = "";
        this.toUpdate = this.toUpdate === "" ? "update" : "";
    }

    // ============================================
    // Render Methods
    // ============================================

    renderFilePatternTooltip(dataInput: string) {
        const tooltipClass = dataInput === "data" ? "data-tooltip" : "trigger-tooltip";
        return html`
    <span class="filePatternTooltip ${tooltipClass}">
        <ul>
            <li>This field is case insensitive.</li>
            <li>Patterns entered in lower case and uppercase are treated the same.</li>
            <li>Use template variables like {businessUnit} for dynamic paths.</li>
            <li>For Wildcard Patterns, include *. Examples: *.zip, *trigger*.txt</li>
            <li>For Absolute Patterns with extension: triggerfile.txt</li>
            <li>For Absolute Patterns without extension, include $ at the end: SLOT199$</li>
            <li>Max length 256 characters.</li>
        </ul>
    </span>
    `;
    }

    renderPatternItem(
        pattern: string,
        isEditDisabled: boolean,
        onEdit: () => void,
        onDelete: () => void,
        editTooltip: string,
        deleteTooltip: string,
        editDataAutoId: string,
        deleteDataAutoId: string
    ) {
        return html`
    <div class="pattern-item">
        <li style="font-size:large;">
            <span title=${pattern}>${pattern}</span>
        </li>
        <jha-tooltip bottom text=${editTooltip}>
            <jha-button data-auto-id=${editDataAutoId} icon sync ?disabled=${this.isReadOnly || isEditDisabled}
                type="button" @click=${onEdit}>
                <jha-icon-edit></jha-icon-edit>
            </jha-button>
        </jha-tooltip>
        <jha-tooltip bottom text=${deleteTooltip}>
            <jha-button data-auto-id=${deleteDataAutoId} icon sync ?disabled=${this.isReadOnly} type="button"
                @click=${onDelete}>
                <jha-icon-delete></jha-icon-delete>
            </jha-button>
        </jha-tooltip>
    </div>
    `;
    }

    renderDataFilePatternSection() {
        // Validation logic:
        // - List empty + input empty = required error
        // - List empty + input has value = prompt to click '+'
        // - List has items + input has value = reminder to add or clear
        // - List has items + input empty = valid (no warning)
        const isDataListEmpty = this.dataFileList.length === 0;
        const hasUnsavedInput = (this.dataFileName || "").trim().length > 0;

        // Determine warning state and message
        let showWarning = false;
        let errorMessage = "";

        if (isDataListEmpty && !hasUnsavedInput) {
            // Required field is empty
            showWarning = false; // Will show via required validation when touched
            errorMessage = "At least one Data File Pattern is required. Enter a pattern and click '+' to add.";
        } else if (isDataListEmpty && hasUnsavedInput) {
            // Has input but nothing in list - needs to click +
            showWarning = true;
            errorMessage = "Click the '+' button to add this pattern to the list.";
        } else if (!isDataListEmpty && hasUnsavedInput) {
            // List has items but there's unsaved input
            showWarning = true;
            errorMessage = "You have an unadded pattern. Click '+' to add it or clear the field.";
        }

        return html`
    <div id="dataFileDiv">
        <div id="dataFileInput">
            <div class="pattern-field">
            <jh-tooltip position="bottom-start"
                label="Enter Data File Pattern. For multiple sources, include parent folder name. Max length 256 characters.">
                <template-string-input data-auto-id="workflowDataFilePatternInput" label="Workflow Data File Pattern"
                    placeholder="eg: *.txt, *.zip, datafile.txt" maxlength="256" .disabled=${this.isReadOnly} .value=${this.dataFileName}
                    .required=${isDataListEmpty} .errorText=${errorMessage} .forceShowWarning=${showWarning}
                    .options=${this.variableOptions} variableButtonTooltipPosition="top-left"
                    @change=${this.onDataFileNameChange} @variable-added=${this.onVariableAdded}
                    @keydown=${(e: KeyboardEvent) => this.onFilePatternKeyDown(e, () => this.onDataFileAdd())}>
                </template-string-input>
            </jh-tooltip>
        <div class="pattern-list">
            <ul>
                ${this.dataFileList?.map((pattern) =>
              this.renderPatternItem(
                pattern,
                this.editDataPattern,
                () => this.onEditDataName(pattern),
                () => this.onDeleteDataName(pattern),
                "Edit Data File Pattern",
                "Delete Data File Pattern",
                "editDataBtn",
                "deleteDataBtn"
              )
            )}
            </ul>
        </div>
    </div>
    
            <!-- Info icon with hover tooltip -->
            <div style="position: relative; margin-top: 32px;">
                <jha-icon-information class="iconInfo" data-auto-id="dataFilePatternInfo"></jha-icon-information>
                ${this.renderFilePatternTooltip("data")}
            </div>
    
            <!-- Add button -->
            <jh-tooltip position="bottom-start" label="Click '+' to add Data File Pattern.">
                <jha-button data-auto-id="addDataBtn" icon sync ?disabled=${this.isReadOnly} type="button"
                    style="margin-top: 32px;" @click=${this.onDataFileAdd}>
                    <jha-icon-plus></jha-icon-plus>
                </jha-button>
            </jh-tooltip>
        </div>
    </div>
    `;
    }

    renderTriggerFilePatternSection() {
        // Validation logic:
        // - List empty + input empty = required error
        // - List empty + input has value = prompt to click '+'
        // - List has items + input has value = reminder to add or clear
        // - List has items + input empty = valid (no warning)
        const isTriggerListEmpty = this.triggerFileList.length === 0;
        const hasUnsavedInput = (this.triggerFileName || "").trim().length > 0;

        // Determine warning state and message
        let showWarning = false;
        let errorMessage = "";

        if (isTriggerListEmpty && !hasUnsavedInput) {
            // Required field is empty
            showWarning = false; // Will show via required validation when touched
            errorMessage = "At least one Trigger File Pattern is required. Enter a pattern and click '+' to add.";
        } else if (isTriggerListEmpty && hasUnsavedInput) {
            // Has input but nothing in list - needs to click +
            showWarning = true;
            errorMessage = "Click the '+' button to add this pattern to the list.";
        } else if (!isTriggerListEmpty && hasUnsavedInput) {
            // List has items but there's unsaved input
            showWarning = true;
            errorMessage = "You have an unadded pattern. Click '+' to add it or clear the field.";
        }

        return html`
        <div id="triggerFileDiv">
            <div id="triggerFileInput">
                <div class="pattern-field">
                <jh-tooltip position="bottom-start"
                    label="Enter Trigger File Pattern. For multiple sources, include parent folder name. Max length 256 characters.">
                    <template-string-input data-auto-id="workflowTriggerFilePatternInput" label="Workflow Trigger File Pattern"
                        placeholder="eg: *.txt, *.zip, triggerfile.txt" maxlength="256" .disabled=${this.isReadOnly}
                        .value=${this.triggerFileName} .required=${isTriggerListEmpty} .errorText=${errorMessage}
                        .forceShowWarning=${showWarning} .options=${this.variableOptions}
                        variableButtonTooltipPosition="top-left" @change=${this.onTriggerFileNameChange}
                        @variable-added=${this.onVariableAdded}
                        @keydown=${(e: KeyboardEvent) => this.onFilePatternKeyDown(e, () => this.onTriggerFileAdd())}>
                    </template-string-input>
                </jh-tooltip>
            <div class="pattern-list">
                <ul>
                    ${this.triggerFileList?.map((pattern) =>
                        this.renderPatternItem(
                            pattern,
                            this.editTriggerPattern,
                            () => this.onEditTriggerName(pattern),
                            () => this.onDeleteTriggerName(pattern),
                            "Edit Trigger File Pattern",
                            "Delete Trigger File Pattern",
                            "editTriggerBtn",
                            "deleteTriggerBtn"
                        )
                    )}
                </ul>
            </div>
        </div>
        
                <!-- Info icon with hover tooltip -->
                <div style="position: relative; margin-top: 32px;">
                    <jha-icon-information class="iconInfo" data-auto-id="triggerFilePatternInfo"></jha-icon-information>
                    ${this.renderFilePatternTooltip("trigger")}
                </div>
        
                <!-- Add button -->
                <jh-tooltip position="bottom-start" label="Click '+' to add Trigger File Pattern.">
                    <jha-button data-auto-id="addTriggerBtn" icon sync ?disabled=${this.isReadOnly} type="button"
                        style="margin-top: 32px;" @click=${this.onTriggerFileAdd}>
                        <jha-icon-plus></jha-icon-plus>
                    </jha-button>
                </jh-tooltip>
            </div>
        </div>
        `;
    }

    render() {
        return html`
    <div>
        <h2>File Patterns</h2>
        <div id="patternDiv">${this.renderDataFilePatternSection()} ${this.renderTriggerFilePatternSection()}</div>
    </div>
    `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "workflow-template-file-pattern-view": WorkflowTemplateFilePatternView;
    }
}
